import { TWO_FACTOR_REQUIRED_ROLES } from '@mogzo/shared';
import { Check, Pencil } from 'lucide-react';
import { type FormEvent, type ReactNode, useCallback, useEffect, useState } from 'react';
import { Link, Navigate, NavLink, useNavigate, useParams, useSearchParams } from 'react-router';
import { ChatSettings } from '../components/chat/ChatSettings';
import { Avatar } from '../components/community/Avatar';
import { AppFrame } from '../components/frame/AppFrame';
import { NotificationSettings } from '../components/notifications/NotificationSettings';
import { TwoFactorSettings } from '../components/TwoFactorSettings';
import { Alert, Button, Field, PageSpinner } from '../components/ui';
import type { Me } from '../lib/api';
import { authClient, authErrorMessage } from '../lib/auth-client';
import { usePublicConfig } from '../lib/public-config';
import { useSession } from '../lib/session';

const ROLE_NAMES: Record<string, string> = {
  player: 'Player',
  developer: 'Developer',
  moderator: 'Moderator',
  reviewer: 'Reviewer',
  super_admin: 'Super admin',
};

const PROVIDER_NAMES: Record<string, string> = { google: 'Google', discord: 'Discord' };

export const cardClass = 'rounded-[18px] border border-line bg-card';
const pillClass =
  'inline-flex min-h-11 items-center justify-center gap-2 rounded-full border border-line bg-card px-5 text-sm font-bold text-ink hover:border-muted';
const linkClass =
  'text-sm font-bold underline decoration-brand decoration-2 underline-offset-4 hover:decoration-ink';

const memberSince = (me: Me) =>
  new Date(me.createdAt).toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });

/** A card's title and the line under it. */
export function CardHead({
  id,
  title,
  children,
}: {
  id: string;
  title: ReactNode;
  children?: ReactNode;
}) {
  return (
    <div>
      <h2 id={id} className="text-lg leading-6 font-bold">
        {title}
      </h2>
      {children && <p className="mt-1 text-sm text-muted">{children}</p>}
    </div>
  );
}

function ConfirmedChip() {
  return (
    <span className="inline-flex h-[22px] items-center gap-1 rounded-full bg-[#e8f5ee] px-2 text-xs font-bold text-[#157244]">
      <Check size={12} strokeWidth={3} aria-hidden="true" />
      Confirmed
    </span>
  );
}

/** One line of the profile: what it is, its value, and where to change it. */
function Row({
  label,
  action,
  children,
}: {
  label: string;
  action?: ReactNode;
  children: ReactNode;
}) {
  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 border-t border-divider px-5 py-4 sm:px-6">
      <dt className="w-40 text-sm font-semibold text-muted">{label}</dt>
      <dd className="min-w-0 flex-[1_1_240px]">{children}</dd>
      {action}
    </div>
  );
}

function ProfileTab({ me }: { me: Me }) {
  const methods = me.signInMethods
    .map((method) => (method === 'credential' ? 'Email and password' : PROVIDER_NAMES[method]))
    .filter(Boolean);
  return (
    <>
      <section aria-labelledby="profile-heading" className={cardClass}>
        <div className="px-5 pt-5 pb-2 sm:px-6">
          <CardHead id="profile-heading" title="Profile">
            The same on mogzo.com, the developer dashboard and the MOGZO CLI.
          </CardHead>
        </div>
        <dl>
          <Row
            label="Username"
            action={
              <Link to="/account/edit" className={linkClass}>
                Change
              </Link>
            }
          >
            <span className="block font-bold break-all">{me.username}</span>
            <span className="mt-0.5 block text-[13px] text-muted">
              Your name on posts, comments and leaderboards.
            </span>
          </Row>
          <Row
            label="Email"
            action={
              <Link to="/account/edit#email" className={linkClass}>
                Change
              </Link>
            }
          >
            <span className="flex flex-wrap items-center gap-2">
              <span className="font-bold break-all">{me.email}</span>
              {me.emailVerified && <ConfirmedChip />}
            </span>
            <span className="mt-0.5 block text-[13px] text-muted">
              Only you and MOGZO see it. Sign-in links and notices go here.
            </span>
          </Row>
          <Row label="Account type">
            <span className="font-bold">
              {me.roles.map((role) => ROLE_NAMES[role] ?? role).join(', ')}
              {me.ageBand === 'teen' && ' · Teen'}
            </span>
          </Row>
        </dl>
      </section>

      <section
        aria-labelledby="signing-in-heading"
        className={`${cardClass} flex flex-wrap items-center gap-x-4 gap-y-3 p-5 sm:px-6`}
      >
        <div className="flex-[1_1_300px]">
          <CardHead id="signing-in-heading" title="Signing in">
            {methods.join(' · ') || 'No sign-in methods yet'} · two-factor{' '}
            {me.twoFactorEnabled ? 'on' : 'off'}. Your password, two-factor and signed-in browsers
            are under Sign-in and security.
          </CardHead>
        </div>
        <Link to="/account/security" className={pillClass}>
          Open
        </Link>
      </section>
    </>
  );
}

function ChangePassword() {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<{ tone: 'error' | 'success'; text: string } | null>(null);

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setMessage(null);
    const { error } = await authClient.changePassword({
      currentPassword: current,
      newPassword: next,
      revokeOtherSessions: true,
    });
    setBusy(false);
    if (error) return setMessage({ tone: 'error', text: authErrorMessage(error) });
    setCurrent('');
    setNext('');
    setMessage({ tone: 'success', text: 'Password changed. Other devices have been signed out.' });
  }

  return (
    <section aria-labelledby="password-heading" className={`${cardClass} p-5 sm:p-6`}>
      <CardHead id="password-heading" title="Password">
        Changing it signs you out everywhere else.
      </CardHead>
      <form onSubmit={onSubmit} className="mt-5 max-w-[420px] space-y-4">
        {message && <Alert tone={message.tone}>{message.text}</Alert>}
        <Field
          label="Current password"
          type="password"
          autoComplete="current-password"
          required
          value={current}
          onChange={(event) => setCurrent(event.target.value)}
        />
        <Field
          label="New password"
          type="password"
          autoComplete="new-password"
          required
          minLength={8}
          maxLength={128}
          hint="At least 8 characters."
          value={next}
          onChange={(event) => setNext(event.target.value)}
        />
        <Button busy={busy}>Change password</Button>
      </form>
    </section>
  );
}

function NoPassword({ me }: { me: Me }) {
  const via = me.signInMethods.map((method) => PROVIDER_NAMES[method]).filter(Boolean);
  return (
    <section
      aria-labelledby="password-heading"
      className={`${cardClass} flex flex-wrap items-center gap-x-4 gap-y-3 p-5 sm:px-6`}
    >
      <div className="flex-[1_1_240px]">
        <CardHead id="password-heading" title="Password">
          None yet{via.length ? `: you sign in with ${via.join(' or ')}` : ''}. We’ll email you a
          link to set one.
        </CardHead>
      </div>
      <Link to="/forgot-password" className={pillClass}>
        Add one
      </Link>
    </section>
  );
}

function WaysToSignIn({ me }: { me: Me }) {
  const config = usePublicConfig();
  const providers = config?.socialProviders ?? [];
  const has = (method: string) => me.signInMethods.includes(method);
  const rows = [
    ...providers.map((provider) => ({
      key: provider,
      name: PROVIDER_NAMES[provider] ?? provider,
      state: has(provider) ? 'Connected' : 'Not connected',
    })),
    {
      key: 'credential',
      name: 'Email and password',
      state: has('credential') ? 'Set up' : 'Add a password to use it',
    },
  ];
  return (
    <section aria-labelledby="ways-heading" className={cardClass}>
      <div className="px-5 pt-5 pb-2 sm:px-6">
        <CardHead id="ways-heading" title="Ways to sign in" />
      </div>
      <ul>
        {rows.map((row) => (
          <li
            key={row.key}
            className="flex items-center justify-between gap-3 border-t border-divider px-5 py-3.5 text-sm sm:px-6"
          >
            <b>{row.name}</b>
            <span className="text-muted">{row.state}</span>
          </li>
        ))}
      </ul>
    </section>
  );
}

interface BrowserSession {
  token: string;
  userAgent?: string | null;
  updatedAt: string | Date;
}

/** “Chrome on Windows” from a user agent, or a plain fallback. */
function browserName(agent: string | null | undefined): string {
  if (!agent) return 'A browser';
  const browser = /Edg\//.test(agent)
    ? 'Edge'
    : /Firefox\//.test(agent)
      ? 'Firefox'
      : /Chrome\//.test(agent)
        ? 'Chrome'
        : /Safari\//.test(agent)
          ? 'Safari'
          : 'A browser';
  const system = /Windows/.test(agent)
    ? 'Windows'
    : /iPhone|iPad/.test(agent)
      ? 'iPhone or iPad'
      : /Android/.test(agent)
        ? 'Android'
        : /Mac OS X/.test(agent)
          ? 'macOS'
          : /Linux/.test(agent)
            ? 'Linux'
            : null;
  return system ? `${browser} on ${system}` : browser;
}

function SignedInBrowsers() {
  const [sessions, setSessions] = useState<BrowserSession[] | null>(null);
  const [current, setCurrent] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const [list, session] = await Promise.all([
        authClient.listSessions(),
        authClient.getSession(),
      ]);
      if (list.error) return setError(authErrorMessage(list.error));
      setSessions((list.data ?? []) as BrowserSession[]);
      setCurrent(session.data?.session.token ?? null);
    } catch {
      setError('Couldn’t load your browsers. Reload the page to try again.');
    }
  }, []);
  useEffect(() => {
    void load();
  }, [load]);

  async function signOutOthers() {
    setBusy(true);
    const { error: failed } = await authClient.revokeOtherSessions();
    setBusy(false);
    if (failed) return setError(authErrorMessage(failed));
    await load();
  }

  const others = (sessions ?? []).filter((session) => session.token !== current);
  return (
    <section aria-labelledby="browsers-heading" className={cardClass}>
      <div className="px-5 pt-5 pb-2 sm:px-6">
        <CardHead id="browsers-heading" title="Browsers signed in">
          Where your account is open on mogzo.com, the developer dashboard and the admin site.
        </CardHead>
      </div>
      {error && (
        <div className="px-5 pb-3 sm:px-6">
          <Alert>{error}</Alert>
        </div>
      )}
      {!sessions && !error && (
        <p role="status" className="px-5 pb-4 text-sm text-muted sm:px-6">
          Loading…
        </p>
      )}
      <ul>
        {sessions?.map((session) => (
          <li key={session.token} className="border-t border-divider px-5 py-3.5 text-sm sm:px-6">
            <b>{browserName(session.userAgent)}</b>{' '}
            {session.token === current && (
              <span className="ml-1 rounded-full bg-[#ebf0fc] px-2 py-0.5 text-xs font-bold text-[#2e5bd0]">
                This browser
              </span>
            )}
            <span className="block text-muted">
              {new Date(session.updatedAt).toLocaleString('en-GB', {
                day: 'numeric',
                month: 'short',
                hour: '2-digit',
                minute: '2-digit',
              })}
            </span>
          </li>
        ))}
      </ul>
      {others.length > 0 && (
        <div className="px-5 pt-1 pb-5 sm:px-6">
          <button
            type="button"
            onClick={signOutOthers}
            disabled={busy}
            className="min-h-11 rounded-full border border-red-200 bg-card px-5 text-sm font-bold text-red-700 hover:bg-red-50 disabled:opacity-60"
          >
            Sign out the others
          </button>
        </div>
      )}
    </section>
  );
}

function SecurityTab({ me }: { me: Me }) {
  const twoFactorRequired = me.roles.some((role) =>
    (TWO_FACTOR_REQUIRED_ROLES as readonly string[]).includes(role),
  );
  return (
    <>
      <TwoFactorSettings enabled={me.twoFactorEnabled} required={twoFactorRequired} />
      {me.signInMethods.includes('credential') ? <ChangePassword /> : <NoPassword me={me} />}
      <WaysToSignIn me={me} />
      <SignedInBrowsers />
    </>
  );
}

const TABS = [
  { path: '/account', label: 'Profile', short: 'Profile' },
  { path: '/account/security', label: 'Sign-in and security', short: 'Security' },
  { path: '/account/notifications', label: 'Notifications', short: 'Notifications' },
  { path: '/account/chat', label: 'Chat', short: 'Chat' },
] as const;

/**
 * mogzo.com/account, /account/security, /account/notifications and /account/chat: who you are,
 * how you sign in, what you're told, and who can message you.
 */
export function Account() {
  const { me, refresh } = useSession();
  const { section } = useParams();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [signingOut, setSigningOut] = useState(false);

  if (me === undefined) return <PageSpinner />;
  if (me === null) return <Navigate to="/sign-in?next=/account" replace />;
  if (!me.profileComplete) return <Navigate to="/complete-profile" replace />;
  const canChat = me?.ageBand === 'adult';
  if (section && !['security', 'notifications', ...(canChat ? ['chat'] : [])].includes(section)) {
    return <Navigate to="/account" replace />;
  }

  async function signOut() {
    setSigningOut(true);
    await authClient.signOut();
    // Leave first: once the session is cleared, this page would redirect to sign-in.
    navigate('/');
    await refresh();
  }

  const signOutButton = (
    <button
      type="button"
      onClick={signOut}
      disabled={signingOut}
      className="min-h-11 rounded-full px-5 text-sm font-bold text-muted hover:text-ink disabled:opacity-60"
    >
      {signingOut ? 'Signing out…' : 'Sign out'}
    </button>
  );

  return (
    <AppFrame>
      <main className="mx-auto flex w-full max-w-[820px] flex-col gap-5 px-4 pt-6 pb-10 sm:px-8 lg:pt-10">
        {params.has('welcome') && (
          <Alert tone="success">
            Welcome to MOGZO, {me.username}! Games and communities are on their way.
          </Alert>
        )}
        {params.get('email') === 'changed' && (
          <Alert tone="success">Your email is now {me.email}.</Alert>
        )}

        <section
          aria-labelledby="account-name"
          className="flex flex-col items-center gap-x-5 gap-y-3 text-center sm:flex-row sm:text-left"
        >
          <Avatar name={me.username} size="xl" />
          <div className="min-w-0 flex-1">
            <h1
              id="account-name"
              className="text-2xl leading-8 font-bold tracking-[-0.015em] break-all sm:text-[30px] sm:leading-9"
            >
              {me.username}
            </h1>
            <p className="mt-1 flex flex-wrap items-center justify-center gap-2 text-sm text-muted sm:justify-start">
              <span className="break-all">{me.email}</span>
              <span aria-hidden="true">·</span>
              <span className="inline-flex h-6 items-center rounded-full border border-line bg-card px-2.5 text-xs font-bold text-ink">
                {ROLE_NAMES[me.roles.at(-1) ?? 'player'] ?? 'Player'}
              </span>
              <span>Member since {memberSince(me)}</span>
            </p>
          </div>
          <div className="flex w-full gap-2 sm:w-auto">
            <Link to="/account/edit" className={`${pillClass} flex-1 sm:flex-none`}>
              <Pencil size={16} strokeWidth={2} aria-hidden="true" />
              Edit profile
            </Link>
            <span className="hidden sm:inline">{signOutButton}</span>
          </div>
        </section>

        <nav
          aria-label="Account pages"
          className="grid grid-cols-3 rounded-full bg-[#ebe8f2] p-1 sm:flex sm:gap-1 sm:rounded-none sm:border-b sm:border-line sm:bg-transparent sm:p-0"
        >
          {TABS.filter((tab) => canChat || tab.path !== '/account/chat').map((tab) => (
            <NavLink
              key={tab.path}
              to={tab.path}
              end
              aria-label={tab.label}
              className={({ isActive }) =>
                `flex h-[38px] items-center justify-center rounded-full text-sm sm:-mb-px sm:h-auto sm:rounded-none sm:border-b-[3px] sm:px-4 sm:py-3 ${isActive ? 'bg-card font-bold shadow-[0_1px_2px_rgba(27,21,47,0.1)] sm:border-ink sm:bg-transparent sm:shadow-none' : 'font-semibold text-muted sm:border-transparent hover:text-ink'}`
              }
            >
              <span className="sm:hidden">{tab.short}</span>
              <span className="hidden sm:inline">{tab.label}</span>
            </NavLink>
          ))}
        </nav>

        {section === 'security' ? (
          <SecurityTab me={me} />
        ) : section === 'notifications' ? (
          <NotificationSettings username={me.username} />
        ) : section === 'chat' ? (
          <ChatSettings />
        ) : (
          <ProfileTab me={me} />
        )}

        <div className="flex justify-center sm:hidden">{signOutButton}</div>
      </main>
    </AppFrame>
  );
}
